<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types'
import { merge } from 'es-toolkit/compat'

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Financialaccount>
  onSubmit: (event: FormSubmitEvent<Partial<Financialaccount>>) => void
  openNestedCallback: (data: Partial<Financialaccount>) => void
  title?: string
  disabledFields?: (keyof Financialaccount | string)[]
  readonly?: boolean
}>()

const { user } = useUserSession()
const { userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))
const simple = ref(true)

const state = ref<Partial<Financialaccount>>(props.data)

const financialaccounts$ = useFinancialaccounts()

const $banks = await useFetch<Bank[]>('/api/db/banks', {
  query: {},
  default: () => [],
})
const $users = await useFetch<User[]>('/api/db/users', {
  query: { type: ['internal'], $columns: ['id', 'name', 'image', 'email'] },
  default: () => [],
})

const utils = useFinancialaccountsUtils({ $banks, $users })

const items = computed<AccordionItem[]>(() => {
  // if (['create'].includes(props.action) && simple.value) {
  //   return [{ slot: 'simple', label: 'Creación rápida', icon: 'i-mdi-lightning-bolt', defaultOpen: true }]
  // }
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
    {
      slot: 'cash',
      label: 'Detalles de Caja',
      icon: 'i-mdi-wallet',
      defaultOpen: true,
      disabled: !state.value.type,
      show: ['cash'], // Bank-related details
    },
    {
      slot: 'bank',
      label: 'Detalles Bancarios',
      icon: 'i-mdi-bank',
      defaultOpen: true,
      disabled: !state.value.type && !state.value.bankId,
      show: ['bank'], // Bank-related details
    },
    {
      slot: 'credit',
      label: 'Detalles de Crédito',
      icon: 'i-mdi-credit-card',
      defaultOpen: true,
      disabled: !state.value.type && !state.value.bankId,
      show: ['credit'], // Only for credit accounts
    },
    {
      slot: 'loan',
      label: 'Detalles de Préstamo',
      icon: 'i-mdi-cash-usd',
      defaultOpen: true,
      disabled: !state.value.type && !state.value.bankId,
      show: ['loan'], // Only for loan accounts
    },
    {
      slot: 'other',
      label: 'Detalles',
      icon: 'i-mdi-cash-usd',
      defaultOpen: true,
      disabled: !state.value.type && !state.value.bankId,
      show: ['other'], // Only for other accounts
    },
  ].filter((item) => {
    let show = true
    if (item.slot !== 'general') {
      show = item.show?.includes(state.value?.type) ?? false
    }
    return show
  })
})

const generateFinancialAccountName = async () => {
  const subtype = utils.maps.subtype[state.value!.type!]?.get(state.value!.subtype!)?.label
  const currency = utils.maps.currency.get(state.value!.currency!)?.value as string
  const bank = state.value.bankId ? utils.maps.bankId.value.get(state.value.bankId)?.name : ''
  const user = state.value.userId ? utils.maps.userId.value.get(state.value.userId)?.name : ''

  let creditType = ''
  if (state.value.type === 'credit' || state.value.type === 'loan') {
    creditType = utils.maps.creditType.get(state.value.creditType!)?.label ?? ''
  }

  const parts = [
    ...(bank ? bank.split(' ') : []),
    ...(subtype ? subtype.split(' ') : []),
    ...(creditType ? creditType.split(' ') : []),
    ...(user ? user.split(' ') : []),
    ...(currency ? currency.split(' ') : []),
  ].filter(Boolean) // Remove empty strings

  state.value.name = parts?.length ? parts.join(' ').toLocaleUpperCase() : ''
}

const loading = ref(false)

const onSubmitFinancialaccount = async (event: FormSubmitEvent<Partial<Financialaccount>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true

  const metaFields = ['createdAt', 'updatedAt', 'archivedAt', 'createdBy', 'updatedBy', 'archivedBy']
  const fieldsToKeep: (keyof Financialaccount)[] = ({
    bank: ['id', 'type', 'subtype', 'name', 'currency', 'bankId', 'accountNumber', 'routingNumber', 'clabe', 'iban', 'balanceMin', 'balanceMax', 'files', ...metaFields],
    cash: ['id', 'type', 'subtype', 'name', 'currency', 'userId', 'balanceMin', 'balanceMax', 'files', ...metaFields],
    credit: ['id', 'type', 'subtype', 'name', 'currency', 'bankId', 'userId', 'accountNumber', 'creditType', 'interestRateType', 'interestRate', 'creditLine', 'issuedDate', 'expiryDate', 'paymentInterval', 'paymentDay', 'files', ...metaFields],
    loan: ['id', 'type', 'subtype', 'name', 'currency', 'bankId', 'accountNumber', 'interestRateType', 'interestRate', 'creditLine', 'issuedDate', 'expiryDate', 'paymentInterval', 'paymentDay', 'files', ...metaFields],
    other: ['id', 'type', 'subtype', 'name', 'currency', 'bankId', 'accountNumber', 'balanceMin', 'balanceMax', 'files', ...metaFields],
  })?.[event.data.type!]

  Object.keys(event.data as Financialaccount).forEach((key) => {
    if (fieldsToKeep.includes(key)) return
    else event.data[key] = undefined
  })

  await props.onSubmit(event)
}
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.financialaccounts"
      :validate-on="['submit']"
      :schema="financialaccounts$.schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitFinancialaccount"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #general>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(financialaccounts$.schema, 'name')"
          >
            <UButtonGroup class="flex">
              <InputText
                v-model.defaultcase="state.name"
                :disabled="disabledFields?.includes('name')"
                class="flex-1"
              />
              <ButtonGenerateString
                :disabled="disabledFields?.includes('name') || !state.type || !state.subtype || !state.currency"
                @click="generateFinancialAccountName"
              />
            </UButtonGroup>
          </UFormGroup>

          <UFormGroup
            label="Tipo"
            name="type"
            :required="isRequired(financialaccounts$.schema, 'type')"
          >
            <SelectMenuBaseInfo
              v-model="state.type"
              :options="utils.options.type"
              autofocus
              :disabled="disabledFields?.includes('type')"
            />
          </UFormGroup>

          <UFormGroup
            label="Subtipo"
            name="subtype"
            :required="isRequired(financialaccounts$.schema, 'subtype')"
          >
            <SelectMenuBaseInfo
              v-model="state.subtype"
              :options="utils.options.subtype[state.type] ?? []"
              :disabled="disabledFields?.includes('subtype') || !state.type"
            />
          </UFormGroup>

          <UFormGroup
            label="Moneda"
            name="currency"
            :required="isRequired(financialaccounts$.schema, 'currency')"
          >
            <SelectMenuBase
              v-model="state.currency"
              :options="optionsSAT.currency"
              :disabled="disabledFields?.includes('currency')"
            />
          </UFormGroup>

          <UFormGroup
            v-if="!['cash', 'other'].includes(state.type!) && state.type"
            label="Banco"
            name="bankId"
            required
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'banks',
                idField: 'bankId',
                utils: utils,
                options: utils.options.bankId.value,
                disabled: disabledFields?.includes('bankId'),
                slideoverOpenCreateProps: {},
                refresh: $banks.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            v-if="['credit-card', 'cash-employee'].includes(state.subtype!) && state.subtype"
            label="Empleado"
            name="userId"
            required
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'users',
                idField: 'userId',
                utils: utils,
                options: utils.options.userId.value ?? [],
                disabled: disabledFields?.includes('userId'),
              }"
            />
          </UFormGroup>
        </template>

        <template #cash>
          <UFormGroup
            label="Balance Mínimo"
            name="balanceMin"
          >
            <UInput
              v-model="state.balanceMin"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMin')"
            />
          </UFormGroup>

          <UFormGroup
            label="Balance Máximo"
            name="balanceMax"
          >
            <UInput
              v-model="state.balanceMax"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMax')"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="financialaccounts"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>

        <template #bank>
          <UFormGroup
            label="Número de cuenta"
            name="accountNumber"
            required
          >
            <InputText
              v-model.defaultcase="state.accountNumber"
              :disabled="disabledFields?.includes('accountNumber')"
            />
          </UFormGroup>

          <UFormGroup
            v-if="['Estados Unidos de America'].includes(utils.maps.bankId.value.get(state.bankId)?.address.country)"
            label="Número de Ruta"
            name="routingNumber"
            required
          >
            <InputText
              v-model.defaultcase="state.routingNumber"
              :disabled="disabledFields?.includes('routingNumber')"
            />
          </UFormGroup>

          <UFormGroup
            v-if="['Mexico'].includes(utils.maps.bankId.value.get(state.bankId)?.address.country)"
            label="CLABE"
            name="clabe"
            required
          >
            <InputText
              v-model.defaultcase="state.clabe"
              :disabled="disabledFields?.includes('clabe')"
            />
          </UFormGroup>

          <UFormGroup
            v-if="!['Estados Unidos de America', 'Mexico'].includes(utils.maps.bankId.value.get(state.bankId)?.address.country)"
            label="Código IBAN"
            name="iban"
            required
          >
            <InputText
              v-model.defaultcase="state.iban"
              :disabled="disabledFields?.includes('iban')"
            />
          </UFormGroup>

          <UFormGroup
            label="Balance Mínimo"
            name="balanceMin"
          >
            <UInput
              v-model="state.balanceMin"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMin')"
            />
          </UFormGroup>

          <UFormGroup
            label="Balance Máximo"
            name="balanceMax"
          >
            <UInput
              v-model="state.balanceMax"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMax')"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="financialaccounts"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>

        <template #credit>
          <UFormGroup
            label="Número de cuenta"
            name="accountNumber"
            required
          >
            <InputText
              v-model.defaultcase="state.accountNumber"
              :disabled="disabledFields?.includes('accountNumber')"
            />
          </UFormGroup>

          <UFormGroup
            label="Tipo de crédito"
            name="creditType"
            required
          >
            <SelectMenuBaseInfo
              v-model="state.creditType"
              :options="utils.options.creditType"
              required
            />
          </UFormGroup>

          <UFormGroup
            label="Tipo de tasa interés"
            name="interestRateType"
            required
          >
            <SelectMenuBaseInfo
              v-model="state.interestRateType"
              :options="utils.options.interestRateType"
              :disabled="disabledFields?.includes('interestRateType')"
            />
          </UFormGroup>

          <UFormGroup
            label="Tasa de interés"
            name="interestRate"
            required
          >
            <UInput
              v-model="state.interestRate"
              type="number"
              trailing-icon="i-mdi-percent"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('interestRate')"
            />
          </UFormGroup>

          <UFormGroup
            label="Línea de crédito"
            name="creditLine"
            required
          >
            <UInput
              v-model="state.creditLine"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('creditLine')"
            />
          </UFormGroup>

          <FieldsetDateRange
            v-model:is-datetime="state.isDatetime"
            v-model:date-start="state.issueDate"
            v-model:date-end="state.expiryDate"
            :is-required-start-date="isRequired(financialaccounts$.schema, 'issueDate')"
            :is-required-end-date="isRequired(financialaccounts$.schema, 'expiryDate')"
            :date-start-label="'Fecha de emisión'"
            :date-end-label="'Fecha de vencimiento'"
            :disabled-fields="['isDatetime']"
          />

          <UFormGroup
            label="Frencuencia de pago"
            name="paymentFrecuency"
            required
          >
            <SelectMenuBaseInfo
              v-model="state.paymentInterval"
              :options="utils.options.paymentInterval"
              :disabled="disabledFields?.includes('paymentInterval')"
            />
          </UFormGroup>

          <UFormGroup
            label="Día de pago"
            name="paymentDay"
          >
            <UInput
              v-model="state.paymentDay"
              type="number"
              inputmode="numeric"
              :min="1"
              :max="31"
              :step="1"
              :disabled="disabledFields?.includes('paymentDay')"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="financialaccounts"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>

        <template #loan>
          <UFormGroup
            label="Número de cuenta"
            name="accountNumber"
            required
          >
            <InputText
              v-model.defaultcase="state.accountNumber"
              :disabled="disabledFields?.includes('accountNumber')"
            />
          </UFormGroup>

          <UFormGroup
            label="Tipo de tasa interés"
            name="interestRateType"
            required
          >
            <SelectMenuBaseInfo
              v-model="state.interestRateType"
              :options="utils.options.interestRateType"
              :disabled="disabledFields?.includes('interestRateType')"
            />
          </UFormGroup>

          <UFormGroup
            label="Tasa de interés"
            name="interestRate"
            required
          >
            <UInput
              v-model="state.interestRate"
              type="number"
              trailing-icon="i-mdi-percent"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('interestRate')"
            />
          </UFormGroup>

          <UFormGroup
            label="Línea de crédito"
            name="creditLine"
            required
          >
            <UInput
              v-model="state.creditLine"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('creditLine')"
            />
          </UFormGroup>

          <FieldsetDateRange
            v-model:is-datetime="state.isDatetime"
            v-model:date-start="state.issueDate"
            v-model:date-end="state.expiryDate"
            :is-required-start-date="isRequired(financialaccounts$.schema, 'issueDate')"
            :is-required-end-date="isRequired(financialaccounts$.schema, 'expiryDate')"
            :date-start-label="'Fecha de emisión'"
            :date-end-label="'Fecha de vencimiento'"
            :disabled-fields="['isDatetime']"
          />

          <UFormGroup
            label="Frencuencia de pago"
            name="paymentFrecuency"
            required
          >
            <SelectMenuBaseInfo
              v-model="state.paymentInterval"
              :options="utils.options.paymentInterval"
              :disabled="disabledFields?.includes('paymentInterval')"
            />
          </UFormGroup>

          <UFormGroup
            label="Día de pago"
            name="paymentDay"
          >
            <UInput
              v-model="state.paymentDay"
              type="number"
              inputmode="numeric"
              :min="1"
              :max="31"
              :step="1"
              :disabled="disabledFields?.includes('paymentDay')"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="financialaccounts"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>

        <template #other>
          <UFormGroup
            label="Número de cuenta"
            name="accountNumber"
            required
          >
            <InputText
              v-model.defaultcase="state.accountNumber"
              :disabled="disabledFields?.includes('accountNumber')"
            />
          </UFormGroup>

          <UFormGroup
            label="Balance Mínimo"
            name="balanceMin"
          >
            <UInput
              v-model="state.balanceMin"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMin')"
            />
          </UFormGroup>

          <UFormGroup
            label="Balance Máximo"
            name="balanceMax"
          >
            <UInput
              v-model="state.balanceMax"
              type="number"
              trailing-icon="i-mdi-currency-usd"
              inputmode="decimal"
              :min="0.00"
              :step="0.01"
              :disabled="disabledFields?.includes('balanceMax')"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="financialaccounts"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>
      </UAccordion>
    </UForm>
    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.financialaccounts"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
